import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { AircraftMake } from '../../types/intellijetnet'
import { ensureArray, executeQuery, executeQueryFirst } from './utils'
import { MarketCoverage } from './market-coverage'

export class AircraftMakesService {
  private static readonly TABLE_NAME = 'aircraft_makes'

  /**
   * Get query builder for aircraft makes with optional market coverage filter
   */
  static query(applyMarketCoverage = true): QueryBuilder {
    const query = intelliJetNetService.from(this.TABLE_NAME)
    
    if (applyMarketCoverage) {
      const makeIds = MarketCoverage.getMakeIds()
      if (makeIds !== null) {
        query.in('make_id', makeIds)
      }
    }
    
    return query
  }

  /**
   * Get make by ID
   */
  static async getById(makeId: number): Promise<AircraftMake | null> {
    return executeQueryFirst<AircraftMake>(
      this.query().eq('make_id', makeId)
    )
  }

  /**
   * Get make by name
   */
  static async getByName(makeName: string): Promise<AircraftMake | null> {
    return executeQueryFirst<AircraftMake>(
      this.query()
        .eq('make_name', makeName)
        .eq('is_active', true)
    )
  }

  /**
   * Get all active makes
   */
  static async getAll(): Promise<AircraftMake[]> {
    return executeQuery<AircraftMake>(
      this.query()
        .eq('is_active', true)
        .order('make_name')
    )
  }

  /**
   * Search makes by name
   */
  static async searchByName(searchTerm: string): Promise<AircraftMake[]> {
    return executeQuery<AircraftMake>(
      this.query()
        .ilike('make_name', `%${searchTerm}%`)
        .eq('is_active', true)
        .order('make_name')
    )
  }

  /**
   * Get makes by manufacturer
   */
  static async getByManufacturer(manufacturer: string): Promise<AircraftMake[]> {
    return executeQuery<AircraftMake>(
      this.query()
        .eq('manufacturer', manufacturer)
        .eq('is_active', true)
        .order('make_name')
    )
  }

  /**
   * Get makes with model count
   */
  static async getMakesWithModelCount(): Promise<Array<AircraftMake & { model_count: number }>> {
    const result = await intelliJetNetService
      .rpc('get_makes_with_model_count')
    return ensureArray<AircraftMake & { model_count: number }>(result)
  }

  /**
   * Get all manufacturers
   */
  static async getManufacturers(): Promise<string[]> {
    const data = await executeQuery<{ manufacturer: string }>(
      intelliJetNetService
        .from(this.TABLE_NAME)
        .select('manufacturer')
        .eq('is_active', true)
    )

    const uniqueManufacturers = [...new Set(data
      .map(item => item.manufacturer)
      .filter(Boolean))]
    
    return uniqueManufacturers.sort()
  }

  /**
   * Get recently updated makes
   */
  static async getRecentlyUpdated(days = 30): Promise<AircraftMake[]> {
    const date = new Date()
    date.setDate(date.getDate() - days)

    return executeQuery<AircraftMake>(
      this.query()
        .gte('updated_at', date.toISOString())
        .eq('is_active', true)
        .order('updated_at', false)
    )
  }

  /**
   * Get make statistics
   */
  static async getStatistics(): Promise<{
    total: number
    byManufacturer: Record<string, number>
  }> {
    const { count: total } = await this.query()
      .eq('is_active', true)
      .count('exact')
      .execute<AircraftMake>()

    // Get by manufacturer
    const result = await intelliJetNetService
      .rpc('get_makes_by_manufacturer_count')
    const mfgData = ensureArray<{ manufacturer: string; count: number }>(result)

    return {
      total: total || 0,
      byManufacturer: mfgData.reduce((acc: Record<string, number>, item) => {
        acc[item.manufacturer] = item.count
        return acc
      }, {})
    }
  }
}

export default AircraftMakesService